/**
 * Database Explorer — AI Assistant contract.
 *
 * Declared here; wired in Phase H. The assistant talks to the linked local
 * `claude` CLI, is given the live schema + protected list + current mode, and
 * RETURNS proposals (prose + fenced ```sql blocks). It never executes — the
 * human approves a proposal and the SQL runs through sql.run → runSql.
 *
 * v1 ships the buffered shape below (one claude -p --output-format=json call).
 * The streaming SSE variant (Response escape hatch) is a target, not required.
 */

import { z } from "zod";
import type { Operation } from "../../types";

export const chatMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string(),
});
export type ChatMessageDto = z.infer<typeof chatMessageSchema>;

export const proposalSchema = z.object({
  sql: z.string(),
  kind: z.enum(["read", "write", "ddl", "unknown"]),
  rationale: z.string(),
});
export type ProposalDto = z.infer<typeof proposalSchema>;

export const chatResponseSchema = z.object({
  /** True when the local `claude` CLI is installed + authed. */
  modelAvailable: z.boolean(),
  model: z.string(),
  explanation: z.string(),
  statements: z.array(proposalSchema),
});
export type ChatResponseDto = z.infer<typeof chatResponseSchema>;

export const chatBody = z.object({
  messages: z.array(chatMessageSchema).min(1),
  includeSampleRows: z.boolean().optional(),
  table: z.string().optional(),
  readOnly: z.boolean().optional(),
});
export type ChatBody = z.infer<typeof chatBody>;

export const assistantChat: Operation<
  void,
  void,
  z.infer<typeof chatBody>,
  z.infer<typeof chatResponseSchema>
> = {
  method: "POST",
  path: () => "/api/database/assistant",
  body: chatBody,
  response: chatResponseSchema,
};

export const assistant = { chat: assistantChat };
